import { useEffect, useRef, type ReactNode } from "react";
import { ArrowRight } from "lucide-react";
import { motion, useReducedMotion, useTransform } from "motion/react";
import { ParticleOrb } from "../components/shared/ParticleOrb";
import { Tag, H1, Body } from "../components/shared/Typography";
import { GhostBtn } from "../components/shared/Buttons";
import { Reveal } from "../components/shared/Reveal";
import { BookDemo } from "../components/demo/BookDemo";
import { useHeroScrollFX } from "../hooks/useHeroScrollFX";
import { useSplitHeadlineReveal } from "../hooks/useSplitHeadlineReveal";
import { useLenis, scrollToId } from "../lib/lenis.tsx";
import { ProblemSection, WhySimulate, FiveServices, StudyFlow } from "../components/foresight/ForesightOverview";
import { AthenaEigen, SimulatorSection, ReliabilityBand, IvizBeam, BuiltToBeChecked } from "../components/foresight/ForesightDetails";
import { TeamsAndAudience, UseCases } from "../components/foresight/ForesightAudience";

const EASE = [0.22, 1, 0.36, 1] as const;
const META_TITLE = "Foresight | Clinical Trial Simulation | Coharts";
const META_DESCRIPTION = "Simulate your trial design on digital twin cohorts before the first patient is enrolled. Model conversion, trial simulation, data exploration and conversational analysis in one platform.";

// Sets this page's <title> and meta description, restoring the site defaults on leave.
function usePageMeta(title: string, description: string) {
  useEffect(() => {
    const meta = document.querySelector('meta[name="description"]');
    const previous = { title: document.title, description: meta?.getAttribute("content") ?? "" };
    document.title = title;
    meta?.setAttribute("content", description);
    return () => { document.title = previous.title; meta?.setAttribute("content", previous.description); };
  }, [title, description]);
}

function Section({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <section id={id} className="py-16 bg-[#FAFAFA] dark:bg-[#050505] scroll-mt-[80px]">
      <Reveal className="max-w-[1400px] mx-auto px-6 lg:px-10">{children}</Reveal>
    </section>
  );
}

function ForesightHero({ onHowItWorks }: { onHowItWorks: () => void }) {
  const reduced = useReducedMotion();
  const { ref, scrollYProgress } = useHeroScrollFX();
  const headingRef = useRef<HTMLHeadingElement>(null);
  useSplitHeadlineReveal(headingRef);
  const visualY = useTransform(scrollYProgress, [0, 1], reduced ? [0, 0] : [0, 60]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.7, 1], reduced ? [1, 1, 1] : [1, 1, 0]);
  const textY = useTransform(scrollYProgress, [0, 0.7, 1], reduced ? [0, 0, 0] : [0, 0, -40]);
  const container = { hidden: {}, show: { transition: { staggerChildren: reduced ? 0 : 0.12, delayChildren: 0.1 } } };
  const item = { hidden: reduced ? { opacity: 0 } : { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } } };
  return (
    <section ref={ref} className="relative min-h-[100dvh] flex items-center overflow-hidden bg-[#FAFAFA] dark:bg-[#050505]">
      <motion.div className="absolute inset-0 z-0" style={{ y: visualY }}>
        <ParticleOrb variant="foresight-sun" accent="#7C4DFF" lightAccent="#5B21B6" offsetX={1.6} scrollProgress={scrollYProgress} />
      </motion.div>
      <div className="relative z-10 max-w-[1800px] mx-auto px-6 lg:px-10 pt-24 pb-16 w-full">
        <motion.div style={{ opacity: textOpacity, y: textY }}>
          <motion.div className="max-w-xl space-y-8" initial="hidden" animate="show" variants={container}>
            <motion.div variants={item}><Tag tone="foresight">Clinical Intelligence · Foresight</Tag></motion.div>
            <H1 ref={headingRef}>
              Simulate success{" "}
              <span className="text-[#5B21B6] dark:text-[#B9A5FF]">before patient one</span>
            </H1>
            <motion.div variants={item}>
              <Body className="text-lg max-w-xl">
                Foresight is Coharts&apos; clinical intelligence platform that unifies Generative Digital Cohorts,
                Agentic System Pharmacology, a High-Performance Clinical Trial Simulator, and
                Intelligent Visualization all in one hub.
              </Body>
            </motion.div>
            <motion.div variants={item} className="flex flex-wrap gap-4">
              <BookDemo location="foresight_page_hero" variant="button" tone="foresight" />
              <GhostBtn onClick={onHowItWorks}>See How It Works <ArrowRight size={16} /></GhostBtn>
            </motion.div>
            <motion.div variants={item} className="inline-flex items-center gap-3 px-4 py-2.5 rounded-full bg-[#0A0A0F]/5 dark:bg-white/5 border border-[#0A0A0F]/10 dark:border-white/10">
              <div className="w-2 h-2 rounded-full bg-[#2D6A52] dark:bg-[#3FA37A] animate-pulse" />
              <span className="text-xs text-[#5C5C66] dark:text-[#9A9A9A] font-medium">FDA-aligned methodology</span>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function ClosingCta() {
  return (
    <section className="bg-[#F3EEFF] px-6 py-24 dark:bg-[#050505] lg:px-10">
      <Reveal className="mx-auto max-w-[1400px]">
        <BookDemo
          location="foresight_page_cta_section"
          variant="card"
          tone="foresight"
          title="See Foresight on a trial like yours."
          description="Walk through how preclinical data becomes a simulation of dose, population, and trial design decisions."
        />
      </Reveal>
    </section>
  );
}

export function ForesightPage() {
  const lenis = useLenis();
  usePageMeta(META_TITLE, META_DESCRIPTION);
  return (
    <div>
      <ForesightHero onHowItWorks={() => scrollToId(lenis, "how-it-works")} />
      <Section><WhySimulate /></Section>
      <Section><FiveServices /></Section>
      <Section><ProblemSection /></Section>
      <Section><AthenaEigen /></Section>
      <Section><SimulatorSection /></Section>
      <Section><ReliabilityBand /></Section>
      <Section><IvizBeam /></Section>
      <Section><BuiltToBeChecked /></Section>
      <Section id="how-it-works"><StudyFlow /></Section>
      <Section><TeamsAndAudience /></Section>
      <Section><UseCases /></Section>
      <ClosingCta />
    </div>
  );
}
